import * as React from 'react';
import { cn } from '@/lib/utils';
import { Label } from './label';

type FieldProps = {
  label: string;
  htmlFor: string;
  error?: string | null;
  hint?: string;
  className?: string;
  children: React.ReactNode;
};

/**
 * Wires the label, hint and error to the control by id so screen readers
 * announce them, and marks the control invalid when there is an error.
 */
export const Field = ({
  label,
  htmlFor,
  error,
  hint,
  className,
  children,
}: FieldProps) => {
  const describedBy =
    [error ? `${htmlFor}-error` : null, hint ? `${htmlFor}-hint` : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>

      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<any>, {
            id: htmlFor,
            'aria-invalid': error ? true : undefined,
            'aria-describedby': describedBy,
          })
        : children}

      {hint && !error ? (
        <p id={`${htmlFor}-hint`} className="text-xs text-ink-subtle">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={`${htmlFor}-error`} className="text-xs text-critical">
          {error}
        </p>
      ) : null}
    </div>
  );
};
